// QuantityCalculatorModal.js  (updated — unit mismatch advisor integrated)
import React, { useState, useEffect, useMemo } from 'react';
import './PageLayout.css';
import LinkCellsModal from '../components/LinkCellsModal';
import UnitMismatchAdvisor from './UnitMismatchAdvisor'; // ✅ NEW
import ConversionHelper from './ConversionHelper';
import { isWorking, multiplyFactor, fmt } from '../utils/conversionRecipes';
import {
  evaluateCell,
  recalcRowTotal,
  buildSourceQtyMap,
  resolveRows,
  healLegacyLinks,
  linkKey,
  buildLinkGraph,
  collectDependents,
} from '../utils/quantityLinks'; // ✅ live links

function QuantityCalculatorModal({
  isOpen,
  onClose,
  onSave,
  initialRows,
  lineItems,
  sectionType,
  currentItemId,      // ✅ NEW — id of the line item this calculator belongs to (cycle prevention)
  lineItemRowsById,   // ✅ NEW — map of itemId -> calculator rows (to detect chains/cycles)
  compositeUnit,  // ✅ NEW prop — the composite's output unit (e.g. 'm2')
  materialUnit,   // ✅ NEW prop — the current line item's unit (e.g. 'm3')
  materialName,   // ✅ NEW prop — the current line item's name (for preset lookup)
  materialWeight, // the line item's Weight pack — its value is the density, in kg per its own unit
}) {
  const [rows, setRows] = useState([]);
  const [helperOpen, setHelperOpen] = useState(false);
  const [linkCellsOpen, setLinkCellsOpen] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const [linkedSourceId, setLinkedSourceId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const baseRows = initialRows.length > 0 ? initialRows : [
        { description: '', count: '', length: '', width: '', height: '', factor: '', total: 0 },
      ];

      // ✅ Auto-heal legacy broken links first: links saved before the stable-key fix point
      // at ids that no longer exist after reload. Re-point them via the "Linked from: X" label
      // so they resolve again (and get persisted on the next Save).
      const { rows: healedRows } = healLegacyLinks(baseRows, lineItems);

      // Track the (healed) linked source for LinkCellsModal's pre-select highlight.
      const rowWithLink = healedRows.find(row => row.linkedSourceId);
      setLinkedSourceId(rowWithLink ? rowWithLink.linkedSourceId : null);

      // ✅ Live links: re-read each linked row's source quantity on open so the
      // calculator reflects the source's CURRENT value, not the value at link time.
      const { rows: resolvedRows } = resolveRows(healedRows, buildSourceQtyMap(lineItems));
      setRows(resolvedRows);
      setHelperOpen(false);
    }
  }, [isOpen, initialRows, lineItems]);

  // ─── Maths helpers ────────────────────────────────────────────────────────

  // numbers, formulas and formulas with units ("250 mm" → 0.25) — see utils/quantityLinks.evaluateCell
  const evaluateFormula = evaluateCell;

  // Row total math lives in utils/quantityLinks so pages and this modal stay in sync.
  const recalcRow = recalcRowTotal;

  // Cycle prevention: ids that already depend on this item must not be offered as link
  // targets, otherwise linking to them would create a circular link.
  const excludeIds = useMemo(() => {
    if (currentItemId == null) return [];
    const graph = buildLinkGraph(lineItems, lineItemRowsById || {});
    return Array.from(collectDependents(graph, currentItemId));
  }, [lineItems, lineItemRowsById, currentItemId]);

  // Warn when a linked row's source no longer exists (deleted / broken link).
  const brokenLinkCount = useMemo(() => {
    const qtyMap = buildSourceQtyMap(lineItems);
    return rows.filter(
      (r) => r && r.linkedSourceId != null && !qtyMap.has(String(r.linkedSourceId))
    ).length;
  }, [rows, lineItems]);

  // ─── Row handlers ─────────────────────────────────────────────────────────

  const handleRowChange = (index, field, value) => {
    const updated = [...rows];

    if (['count', 'length', 'width', 'height', 'factor'].includes(field)) {
      updated[index][field] = value;
      const ev = evaluateFormula(value.startsWith('=') ? value.slice(1) : value);
      updated[index][`${field}_evaluated`] = ev;
    } else {
      updated[index][field] = value;
    }

    updated[index].total = recalcRow(updated[index]);
    setRows(updated);
  };

  const addRow = () => {
    setRows([
      ...rows,
      { description: '', count: '', length: '', width: '', height: '', factor: '', total: 0 },
    ]);
  };

  const deleteRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  // ✅ Unlink a row: stop following the source but keep the current value as a normal,
  // editable length so the user doesn't lose their number.
  const handleUnlink = (index) => {
    const updated = [...rows];
    const rest = { ...updated[index] };
    delete rest.linkedSourceId;
    rest.total = recalcRow(rest);
    updated[index] = rest;
    setRows(updated);

    const stillLinked = updated.find((row) => row.linkedSourceId);
    setLinkedSourceId(stillLinked ? stillLinked.linkedSourceId : null);
  };

  // ─── Link Cells handlers ──────────────────────────────────────────────────

  const handleLinkCellsClick = (rowIndex) => {
    setActiveRowIndex(rowIndex);
    setLinkCellsOpen(true);
  };

  const handleQuantitySelect = (quantity, selectedItem) => {
    if (activeRowIndex !== null) {
      const updated = [...rows];

      const getField = (item, lc, uc) => item?.[lc] ?? item?.[uc];
      const itemDescription = getField(selectedItem, 'description', 'CostMeasurementName');
      // ✅ Store the STABLE link key so the value saved here matches what resolveRows/
      // buildSourceQtyMap later resolve on reload (prevents links breaking on next load).
      const itemId = linkKey(selectedItem);

      updated[activeRowIndex].length = quantity.toString();
      updated[activeRowIndex].length_evaluated = quantity;
      updated[activeRowIndex].linkedSourceId = itemId;
      setLinkedSourceId(itemId);

      if (!updated[activeRowIndex].description) {
        updated[activeRowIndex].description = `Linked from: ${itemDescription || 'Unknown'}`;
      }

      updated[activeRowIndex].total = recalcRow(updated[activeRowIndex]);

      setRows(updated);
      setActiveRowIndex(null);
    }
  };

  // ─── ✅ NEW: UnitMismatchAdvisor handler ──────────────────────────────────

  /**
   * Called when the user clicks "Apply" in the UnitMismatchAdvisor.
   * Inserts a new calculator row pre-filled with the conversion factor.
   *
   * @param {number} factorValue  - the calculated conversion factor (e.g. 0.1 for 100mm)
   * @param {string} field        - which calculator field to populate ('factor' | 'length')
   * @param {string} description  - auto-generated description for the row
   */
  const handleApplyConversionFactor = (factorValue, field, description) => {
    const newRow = {
      description,
      count: '',
      length: '',
      width: '',
      height: '',
      factor: '',
      total: 0,
    };

    // Pre-fill the appropriate field
    newRow[field] = String(factorValue);
    newRow[`${field}_evaluated`] = factorValue;

    // Count defaults to 1 so total = factor (useful immediate feedback)
    newRow.count = '1';
    newRow.count_evaluated = 1;

    newRow.total = recalcRow(newRow);

    setRows(prev => [...prev, newRow]);
  };

  // ─── Conversion helper ────────────────────────────────────────────────────

  const NUMERIC_FIELDS = ['count', 'length', 'width', 'height', 'factor'];
  const materialise = (r) => {
    const out = { ...r };
    NUMERIC_FIELDS.forEach(f => { out[`${f}_evaluated`] = evaluateFormula(out[f]); });
    out.total = recalcRow(out);
    return out;
  };
  const isBlankRow = (r) => !r.linkedSourceId && !String(r.description || '').trim()
    && NUMERIC_FIELDS.every(f => String(r[f] ?? '').trim() === '');

  /** Rows worked out by the helper go under the existing ones (replacing the empty starter row). */
  const handleAddHelperRows = (newRows) => {
    setRows(prev => [...prev.filter(r => !isBlankRow(r)), ...newRows.map(materialise)]);
  };

  /** Waste / allowance: every row that has a quantity gets its Factor multiplied. */
  const handleMultiplyRows = (multiplier) => {
    setRows(prev => prev.map(r => (r.total ? materialise({ ...r, factor: multiplyFactor(r.factor, multiplier) }) : r)));
  };

  // small conversion results (0.0153 m³) must not round away to 0.02
  const formatTotal = (t) => (Math.abs(t) >= 100 ? t.toFixed(2) : fmt(Number(t.toPrecision(4))));

  // ─── Save ─────────────────────────────────────────────────────────────────

  const handleSave = () => {
    const totalQuantity = rows.reduce((sum, row) => {
      const rowTotal = parseFloat(row.total);
      return sum + (isNaN(rowTotal) ? 0 : rowTotal);
    }, 0);
    onSave(totalQuantity, rows);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>Quantity Calculator — {sectionType}</h3>

          {/* ✅ NEW: Unit mismatch advisor — appears only when units differ */}
          <UnitMismatchAdvisor
            compositeUnit={compositeUnit}
            materialUnit={materialUnit}
            materialName={materialName}
            onApplyFactor={handleApplyConversionFactor}
          />

          {helperOpen && (
            <ConversionHelper
              compositeUnit={compositeUnit}
              materialUnit={materialUnit}
              materialName={materialName}
              materialWeight={materialWeight}
              onAddRows={handleAddHelperRows}
              onMultiplyRows={handleMultiplyRows}
              onClose={() => setHelperOpen(false)}
            />
          )}

          {brokenLinkCount > 0 && (
            <p style={{ color: '#b45309', fontWeight: 600, margin: '4px 0' }}>
              ⚠️ {brokenLinkCount} linked row{brokenLinkCount === 1 ? '' : 's'} can&apos;t find
              {brokenLinkCount === 1 ? ' its' : ' their'} source line item (it may have been
              deleted). The last value is kept — use ✖ to unlink and edit manually.
            </p>
          )}

          <table className="calculator-table">
            <thead>
              <tr>
                <th>DESCRIPTION</th>
                <th>COUNT</th>
                <th>LENGTH</th>
                <th>WIDTH</th>
                <th>HEIGHT</th>
                <th>FACTOR</th>
                <th>TOTAL</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className={row.total === 0 ? 'note-row' : ''}>
                  <td>
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) => handleRowChange(index, 'description', e.target.value)}
                      placeholder=""
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.count}
                      onChange={(e) => handleRowChange(index, 'count', e.target.value)}
                      placeholder="0"
                      title={isWorking(row.count) ? `= ${fmt(Number(row.count_evaluated) || 0)}` : undefined}
                    />
                    {isWorking(row.count) && <span className="calc-cell-value">= {fmt(Number(row.count_evaluated) || 0)}</span>}
                  </td>
                  <td>
                    <div className="input-with-link">
                      <input
                        type="text"
                        value={row.length}
                        onChange={(e) => handleRowChange(index, 'length', e.target.value)}
                        placeholder="0"
                        readOnly={!!row.linkedSourceId}
                        className={row.linkedSourceId ? 'linked-input' : ''}
                        title={row.linkedSourceId
                          ? "Live-linked to another line item's quantity. Use 🔗 to relink or ✖ to unlink."
                          : ''}
                      />
                      <button
                        className="btn-link-cell"
                        onClick={() => handleLinkCellsClick(index)}
                        title={row.linkedSourceId ? 'Change linked line item' : 'Link to line item quantity'}
                        type="button"
                      >
                        🔗
                      </button>
                      {row.linkedSourceId && (
                        <button
                          className="btn-link-cell btn-unlink-cell"
                          onClick={() => handleUnlink(index)}
                          title="Unlink (keep current value, stop following the source)"
                          type="button"
                        >
                          ✖
                        </button>
                      )}
                    </div>
                    {!row.linkedSourceId && isWorking(row.length) && (
                      <span className="calc-cell-value">= {fmt(Number(row.length_evaluated) || 0)}</span>
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.width}
                      onChange={(e) => handleRowChange(index, 'width', e.target.value)}
                      placeholder="0"
                      title={isWorking(row.width) ? `= ${fmt(Number(row.width_evaluated) || 0)}` : undefined}
                    />
                    {isWorking(row.width) && <span className="calc-cell-value">= {fmt(Number(row.width_evaluated) || 0)}</span>}
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.height}
                      onChange={(e) => handleRowChange(index, 'height', e.target.value)}
                      placeholder="0"
                      title={isWorking(row.height) ? `= ${fmt(Number(row.height_evaluated) || 0)}` : undefined}
                    />
                    {isWorking(row.height) && <span className="calc-cell-value">= {fmt(Number(row.height_evaluated) || 0)}</span>}
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.factor}
                      onChange={(e) => handleRowChange(index, 'factor', e.target.value)}
                      placeholder="0"
                      title={isWorking(row.factor) ? `= ${fmt(Number(row.factor_evaluated) || 0)}` : undefined}
                    />
                    {isWorking(row.factor) && <span className="calc-cell-value">= {fmt(Number(row.factor_evaluated) || 0)}</span>}
                  </td>
                  <td className={row.total === 0 ? 'total-zero' : ''}>
                    {row.total === 0 ? '-' : formatTotal(row.total)}
                  </td>
                  <td>
                    <button className="quantity-calc-buttons" onClick={() => deleteRow(index)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="calc-tip">
            Cells take formulas and units — <code>=120/7850</code>, <code>250 mm</code>, <code>2 * 0.6 m</code>.
            For conversions (kg → m³, litres, items per m²) and parts missing from the model, use the Conversion helper.
          </p>

          <div className="calculator-actions">
            <button className="quantity-calc-buttons" onClick={addRow}>Add Row</button>
            <button
              type="button"
              className={`ch-toggle ${helperOpen ? 'ch-toggle--active' : ''}`}
              onClick={() => setHelperOpen(o => !o)}
              title="Work out a quantity from a mass, litres, item size, % or dimensions"
            >
              🧮 Conversion helper
            </button>
          </div>

          <div className="modal-actions">
            <button className="quantity-calc-buttons" onClick={handleSave}>Save</button>
            <button className="quantity-calc-buttons" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>

      <LinkCellsModal
        isOpen={linkCellsOpen}
        onClose={() => { setLinkCellsOpen(false); setActiveRowIndex(null); }}
        onSelectQuantity={handleQuantitySelect}
        lineItems={lineItems || []}
        sectionType={sectionType}
        linkedSourceId={linkedSourceId}
        excludeIds={excludeIds}
        currentItemId={currentItemId}
      />
    </>
  );
}

export default QuantityCalculatorModal;